/**
 * Genereert srcset + src via images.weserv.nl zodat de browser een kleinere,
 * WebP-gecomprimeerde variant kan downloaden. De weergegeven afmetingen
 * (width/height attributen, CSS-classes) blijven volledig onaangeroerd —
 * dit versnelt alleen de dataoverdracht, niet de layout of de zichtbare
 * grootte van de afbeelding.
 *
 * Werkt met zowel absolute (https://…) als project-relatieve (/__l5e/…) URL's.
 * Voor relatieve URL's wordt de productiedomeinnaam gebruikt zodat weserv de
 * bron kan ophalen (op preview/dev valt de browser terug op de originele src).
 */
const WESERV = "https://images.weserv.nl/";
// Lovable CDN-assets zijn publiek bereikbaar via de lovable.app-origin,
// óók vanaf de custom domain-deployment. Het custom domein zelf geeft
// 421 terug voor /__l5e/ paden, dus weserv moet expliciet de lovable.app
// origin gebruiken als upstream.
const ASSET_ORIGIN_HOST = "elvion-pro.lovable.app";

function toUpstream(url: string): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\//, "");
  }
  if (url.startsWith("/__l5e/")) {
    return `${ASSET_ORIGIN_HOST}${url}`;
  }
  // Vite-gebundelde assets (bijv. /assets/team-DjgEbdZW.webp) zijn publiek
  // bereikbaar via de lovable.app-origin en kunnen dus ook via weserv worden
  // uitgeleverd in kleinere/gecomprimeerde varianten.
  if (url.startsWith("/assets/")) {
    return `${ASSET_ORIGIN_HOST}${url}`;
  }
  return null;
}

export function responsiveImage(
  url: string,
  widths: number[] = [200, 400, 600, 800],
  opts: { quality?: number; format?: "webp" | "auto"; sharpen?: number } = {},
): { src: string; srcSet: string } | null {
  const upstream = toUpstream(url);
  if (!upstream) return null;
  const q = opts.quality ?? 78;
  const fmt = opts.format ?? "webp";
  // weserv `sharp` param: 1-10, past een unsharp-mask toe. Nuttig bij foto's
  // die door meerdere compressierondes wat wazig zijn geworden.
  const sharpen = opts.sharpen && opts.sharpen > 0 ? `&sharp=${opts.sharpen}` : "";
  const encoded = encodeURIComponent(upstream);
  const build = (w: number) =>
    `${WESERV}?url=${encoded}&w=${w}&output=${fmt}&q=${q}${sharpen}&we`;
  const srcSet = widths.map((w) => `${build(w)} ${w}w`).join(", ");
  const largest = widths[widths.length - 1];
  return { src: build(largest), srcSet };
}
